import React, { useState, useEffect } from 'react';
import { apiFetch } from './utils/api';
import PageHeader from './components/PageHeader';

function SettingsSalesOrders() {
  const [statuses, setStatuses] = useState([]);
  const [deliveryMethods, setDeliveryMethods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newStatus, setNewStatus] = useState({ name: '', color: '#3B82F6', is_default: false });
  const [newDelivery, setNewDelivery] = useState({ name: '', charge: 0, is_default: false });
  const [editingStatus, setEditingStatus] = useState(null);
  const [editingDelivery, setEditingDelivery] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statusRes, deliveryRes] = await Promise.all([
        apiFetch('/api/sales-order-statuses'),
        apiFetch('/api/delivery-methods')
      ]);

      const statusData = await statusRes.json();
      const deliveryData = await deliveryRes.json();

      setStatuses(Array.isArray(statusData) ? statusData : []);
      setDeliveryMethods(Array.isArray(deliveryData) ? deliveryData : []);
    } catch (err) {
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  };

  const addStatus = async () => {
    if (!newStatus.name.trim()) {
      alert('Please enter a status name');
      return;
    }

    setSaving(true);
    try {
      const res = await apiFetch('/api/sales-order-statuses', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newStatus)
      });

      if (!res.ok) throw new Error('Failed to add status');

      setNewStatus({ name: '', color: '#3B82F6', is_default: false });
      fetchData();
    } catch (err) {
      console.error('Error adding status:', err);
      alert('Failed to add status');
    } finally {
      setSaving(false);
    }
  };

  const updateStatus = async (id, updates) => {
    setSaving(true);
    try {
      const res = await apiFetch(`/api/sales-order-statuses/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates)
      });

      if (!res.ok) throw new Error('Failed to update status');

      setEditingStatus(null);
      fetchData();
    } catch (err) {
      console.error('Error updating status:', err);
      alert('Failed to update status');
    } finally {
      setSaving(false);
    }
  };

  const deleteStatus = async (id) => {
    if (!window.confirm('Are you sure you want to delete this status?')) return;

    setSaving(true);
    try {
      const res = await apiFetch(`/api/sales-order-statuses/${id}`, {
        method: 'DELETE'
      });

      if (!res.ok) throw new Error('Failed to delete status');

      fetchData();
    } catch (err) {
      console.error('Error deleting status:', err);
      alert('Failed to delete status');
    } finally {
      setSaving(false);
    }
  };

  const addDeliveryMethod = async () => {
    if (!newDelivery.name.trim()) {
      alert('Please enter a delivery method name');
      return;
    }

    setSaving(true);
    try {
      const res = await apiFetch('/api/delivery-methods', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newDelivery)
      });

      if (!res.ok) throw new Error('Failed to add delivery method');

      setNewDelivery({ name: '', charge: 0, is_default: false });
      fetchData();
    } catch (err) {
      console.error('Error adding delivery method:', err);
      alert('Failed to add delivery method');
    } finally {
      setSaving(false);
    }
  };

  const updateDeliveryMethod = async (id, updates) => {
    setSaving(true);
    try {
      const res = await apiFetch(`/api/delivery-methods/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates)
      });

      if (!res.ok) throw new Error('Failed to update delivery method');

      setEditingDelivery(null);
      fetchData();
    } catch (err) {
      console.error('Error updating delivery method:', err);
      alert('Failed to update delivery method');
    } finally {
      setSaving(false);
    }
  };

  const deleteDeliveryMethod = async (id) => {
    if (!window.confirm('Are you sure you want to delete this delivery method?')) return;

    setSaving(true);
    try {
      const res = await apiFetch(`/api/delivery-methods/${id}`, {
        method: 'DELETE'
      });

      if (!res.ok) throw new Error('Failed to delete delivery method');

      fetchData();
    } catch (err) {
      console.error('Error deleting delivery method:', err);
      alert('Failed to delete delivery method');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="p-6 text-center text-gray-500">Loading...</div>;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader
        title="Sales Order Settings"
        subtitle="Statuses and delivery methods"
        backTo="/settings/operations-files"
        backLabel="Operations Setup"
        sticky
      />

      <div className="max-w-6xl mx-auto p-6 space-y-6">

        {/* Sales Order Statuses */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Sales Order Statuses</h2>

          {/* Add New Status */}
          <div className="mb-6 p-4 bg-gray-50 rounded-lg">
            <h3 className="font-semibold text-gray-700 mb-3">Add New Status</h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <input
                type="text"
                value={newStatus.name}
                onChange={(e) => setNewStatus({ ...newStatus, name: e.target.value })}
                placeholder="Status name (e.g., Pending)"
                className="px-3 py-2 border border-gray-300 rounded text-sm focus:ring-1 focus:ring-blue-500"
              />
              <input
                type="color"
                value={newStatus.color}
                onChange={(e) => setNewStatus({ ...newStatus, color: e.target.value })}
                className="h-10 w-full border border-gray-300 rounded cursor-pointer"
              />
              <label className="flex items-center gap-2 px-3 py-2 border border-gray-300 rounded bg-white">
                <input
                  type="checkbox"
                  checked={newStatus.is_default}
                  onChange={(e) => setNewStatus({ ...newStatus, is_default: e.target.checked })}
                  className="rounded"
                />
                <span className="text-sm">Default</span>
              </label>
              <button
                onClick={addStatus}
                disabled={saving}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded font-medium disabled:opacity-50"
              >
                Add Status
              </button>
            </div>
          </div>

          {/* Status List */}
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-100">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status Name</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Color</th>
                  <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">Default</th>
                  <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase w-32">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {statuses.length === 0 ? (
                  <tr>
                    <td colSpan="4" className="px-4 py-8 text-center text-gray-500">
                      No statuses configured. Add one above to get started.
                    </td>
                  </tr>
                ) : (
                  statuses.map(status => (
                    <tr key={status.id}>
                      {editingStatus?.id === status.id ? (
                        <>
                          <td className="px-4 py-2">
                            <input
                              type="text"
                              value={editingStatus.name}
                              onChange={(e) => setEditingStatus({ ...editingStatus, name: e.target.value })}
                              className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                            />
                          </td>
                          <td className="px-4 py-2">
                            <input
                              type="color"
                              value={editingStatus.color}
                              onChange={(e) => setEditingStatus({ ...editingStatus, color: e.target.value })}
                              className="h-8 w-20 border border-gray-300 rounded cursor-pointer"
                            />
                          </td>
                          <td className="px-4 py-2 text-center">
                            <input
                              type="checkbox"
                              checked={editingStatus.is_default}
                              onChange={(e) => setEditingStatus({ ...editingStatus, is_default: e.target.checked })}
                              className="rounded"
                            />
                          </td>
                          <td className="px-4 py-2 text-center">
                            <button
                              onClick={() => updateStatus(status.id, editingStatus)}
                              className="text-green-600 hover:text-green-800 text-sm mr-2"
                            >
                              ✓ Save
                            </button>
                            <button
                              onClick={() => setEditingStatus(null)}
                              className="text-gray-600 hover:text-gray-800 text-sm"
                            >
                              ✗ Cancel
                            </button>
                          </td>
                        </>
                      ) : (
                        <>
                          <td className="px-4 py-2">
                            <span className="font-medium text-gray-900">{status.name}</span>
                          </td>
                          <td className="px-4 py-2">
                            <div className="flex items-center gap-2">
                              <div
                                className="w-6 h-6 rounded border border-gray-300"
                                style={{ backgroundColor: status.color }}
                              ></div>
                              <span className="text-xs text-gray-500 font-mono">{status.color}</span>
                            </div>
                          </td>
                          <td className="px-4 py-2 text-center">
                            {status.is_default && (
                              <span className="inline-block px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded">
                                Default
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-2 text-center">
                            <button
                              onClick={() => setEditingStatus(status)}
                              className="text-blue-600 hover:text-blue-800 text-sm mr-2"
                            >
                              ✏️ Edit
                            </button>
                            <button
                              onClick={() => deleteStatus(status.id)}
                              className="text-red-600 hover:text-red-800 text-sm"
                            >
                              🗑️
                            </button>
                          </td>
                        </>
                      )}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Delivery Methods */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Delivery Methods</h2>

          {/* Add New Delivery Method */}
          <div className="mb-6 p-4 bg-gray-50 rounded-lg">
            <h3 className="font-semibold text-gray-700 mb-3">Add New Delivery Method</h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <input
                type="text"
                value={newDelivery.name}
                onChange={(e) => setNewDelivery({ ...newDelivery, name: e.target.value })}
                placeholder="Method name (e.g., Standard Delivery)"
                className="px-3 py-2 border border-gray-300 rounded text-sm focus:ring-1 focus:ring-blue-500"
              />
              <input
                type="number"
                value={newDelivery.charge}
                onChange={(e) => setNewDelivery({ ...newDelivery, charge: parseFloat(e.target.value) || 0 })}
                placeholder="Charge (£)"
                step="0.01"
                className="px-3 py-2 border border-gray-300 rounded text-sm focus:ring-1 focus:ring-blue-500"
              />
              <label className="flex items-center gap-2 px-3 py-2 border border-gray-300 rounded bg-white">
                <input
                  type="checkbox"
                  checked={newDelivery.is_default}
                  onChange={(e) => setNewDelivery({ ...newDelivery, is_default: e.target.checked })}
                  className="rounded"
                />
                <span className="text-sm">Default</span>
              </label>
              <button
                onClick={addDeliveryMethod}
                disabled={saving}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded font-medium disabled:opacity-50"
              >
                Add Method
              </button>
            </div>
          </div>

          {/* Delivery Methods List */}
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-100">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Method Name</th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Charge</th>
                  <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">Default</th>
                  <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase w-32">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {deliveryMethods.length === 0 ? (
                  <tr>
                    <td colSpan="4" className="px-4 py-8 text-center text-gray-500">
                      No delivery methods configured. Add one above to get started.
                    </td>
                  </tr>
                ) : (
                  deliveryMethods.map(method => (
                    <tr key={method.id}>
                      {editingDelivery?.id === method.id ? (
                        <>
                          <td className="px-4 py-2">
                            <input
                              type="text"
                              value={editingDelivery.name}
                              onChange={(e) => setEditingDelivery({ ...editingDelivery, name: e.target.value })}
                              className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                            />
                          </td>
                          <td className="px-4 py-2">
                            <input
                              type="number"
                              value={editingDelivery.charge}
                              onChange={(e) => setEditingDelivery({ ...editingDelivery, charge: parseFloat(e.target.value) || 0 })}
                              step="0.01"
                              className="w-full px-2 py-1 border border-gray-300 rounded text-sm text-right"
                            />
                          </td>
                          <td className="px-4 py-2 text-center">
                            <input
                              type="checkbox"
                              checked={editingDelivery.is_default}
                              onChange={(e) => setEditingDelivery({ ...editingDelivery, is_default: e.target.checked })}
                              className="rounded"
                            />
                          </td>
                          <td className="px-4 py-2 text-center">
                            <button
                              onClick={() => updateDeliveryMethod(method.id, editingDelivery)}
                              className="text-green-600 hover:text-green-800 text-sm mr-2"
                            >
                              ✓ Save
                            </button>
                            <button
                              onClick={() => setEditingDelivery(null)}
                              className="text-gray-600 hover:text-gray-800 text-sm"
                            >
                              ✗ Cancel
                            </button>
                          </td>
                        </>
                      ) : (
                        <>
                          <td className="px-4 py-2">
                            <span className="font-medium text-gray-900">{method.name}</span>
                          </td>
                          <td className="px-4 py-2 text-right">
                            <span className="font-medium text-gray-900">£{parseFloat(method.charge || 0).toFixed(2)}</span>
                          </td>
                          <td className="px-4 py-2 text-center">
                            {method.is_default && (
                              <span className="inline-block px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded">
                                Default
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-2 text-center">
                            <button
                              onClick={() => setEditingDelivery(method)}
                              className="text-blue-600 hover:text-blue-800 text-sm mr-2"
                            >
                              ✏️ Edit
                            </button>
                            <button
                              onClick={() => deleteDeliveryMethod(method.id)}
                              className="text-red-600 hover:text-red-800 text-sm"
                            >
                              🗑️
                            </button>
                          </td>
                        </>
                      )}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

      </div>
    </div>
  );
}

export default SettingsSalesOrders;
